import formDataEntries from 'form-data-entries';
import Numeral from 'numeral';

import HTTPService from './services/HTTPService.es6';
import spinnerTemplate from './templates/spinner-dots.html';
import { addClass, removeClass } from './domHelpers.es6';
import MAEvent from './ma-event';

require('jquery-form')();

/**
 * Extract values from given form.
 * ⚠️ input[file] value will be undefined (see https://github.com/github/form-data-entries#undefined-behavior)
 * @param  {Element} form Form
 * @return {Object} key/value content of form
 */
export function getFormValues(form) {
    if (!form) {
        return {};
    }

    return formDataEntries(form).reduce((previous, [key, value]) => {
        // eslint-disable-next-line no-param-reassign
        previous[key] = value;
        return previous;
    }, {});
}

/**
 *
 * @param {JQuery} $items
 */
function removeErrorClass($items) {
    $items.removeClass($items.data('error-class') || 'error');
}

/**
 * Remove one error
 * @param {String} parentSelector domSelector of div containing error
 * @param {String} errorSelector errorSelector to select error in domLocation
 */
function removeError(parentSelector, errorSelector = '[data-field-error]') {
    const errorContainer = document.querySelector(parentSelector);
    const error = errorContainer ? errorContainer.querySelector(errorSelector) : null;

    if (error) {
        error.parentNode.removeChild(error);
    }
}

/**
 *
 * @param {Jquery} $form
 */
export function removeErrors($form) {
    $form.find('small').remove();

    const $groups = $form.find('[data-control-group]');
    const $fields = $groups.find('[data-control]');
    const $labels = $groups.find('label');
    removeErrorClass($groups);
    removeErrorClass($fields);
    removeErrorClass($labels);
}

/**
 * Display an error for a specific field
 *
 * @param {jQuery} $field The field the error to be displayed
 * @param {array} errorMessages List of errors messages. Pass empty to remove error
 * @private
 */
export function setFieldError($field, errorMessages) {
    if (!Array.isArray(errorMessages)) {
        return;
    }

    const $group = $field.parents('[data-control-group]');
    const $fieldParent = $field.parent(); // select direct parent to color border
    const $label = $group.find('label');
    const $errorPlaceholder = $group.find('[data-control-errors]');
    const errorString = `<small data-field-error class="error field__errors">${errorMessages.join(
        '<br>',
    )}</small>`;

    if (errorMessages.length === 0) {
        $group.find('small').remove();
        // removing error classes from the DOM elements when there is no error
        $group.removeClass($group.data('error-class') || 'error');
        $field.removeClass($group.data('error-class') || 'error');
        $fieldParent.removeClass($fieldParent.data('error-class') || 'error');
        $label.removeClass($label.data('error-class') || 'error');
        return;
    }

    $group.addClass($group.data('error-class') || 'error');
    $field.addClass($field.data('error-class') || 'error');
    $fieldParent.addClass($fieldParent.data('error-class') || 'error');
    $label.addClass($label.data('error-class') || 'error');

    if ($errorPlaceholder.length > 0) {
        $errorPlaceholder.html(errorString);
    } else {
        $field.parents('[data-control]').append(errorString);
    }
}

/**
 * Display one error in the given location
 * @param {String} domLocationSelector dom Selector to select element containing error
 * @param {String} errorMessage error message you wish to display
 * @param {Boolean} withTopMargin to add extra margin top to message error
 */

export function displayError(domLocationSelector, errorMessage, withTopMargin = false) {
    const errorString = `<small data-field-error class="error field__errors ${
        withTopMargin ? 'margin-top-double' : ''
    }">${errorMessage}</small>`;
    const errorContainer = document.querySelector(domLocationSelector);

    if (errorContainer) {
        errorContainer.innerHTML = errorString;
    }
}

/**
 *
 * @param {jQuery} $form The jQuery object representing the form
 * @param {object} errors List of fields errors
 */
export function displayErrors($form, errors) {
    let $field;

    removeErrors($form);

    const fieldNames = Object.keys(errors);
    fieldNames.forEach(fieldName => {
        $field = $form.find(`[name="${fieldName}"]`);
        setFieldError($field, errors[fieldName]);
    });
}

/**
 *
 * @param {jQuery} $form
 * @param {function} successCallback
 * @param {function} error400Callback
 * @param {function} errorCallback
 * @deprecated Will be remove. Use bindAjaxForm instead
 */
export function bindPopinFormEvent($form, successCallback, error400Callback, errorCallback) {
    const $container = $form.parents('[data-reveal]');
    $form.ajaxForm({
        success(response, textStatus, jqXHR) {
            $form.hide();
            $container.find('[data-message-success]').removeClass('hide');
            $form.trigger('submit_success');
            if (typeof successCallback === 'function') {
                successCallback(response, textStatus, jqXHR);
            }
        },
        error(jqXHR, textStatus, errorThrown) {
            if (jqXHR.status === 400) {
                $form.trigger('submit_error');
                $container.html(jqXHR.responseText);
                bindPopinFormEvent(
                    $container.find('form'),
                    successCallback,
                    error400Callback,
                    errorCallback,
                );
                if (typeof error400Callback === 'function') {
                    error400Callback(jqXHR, textStatus, errorThrown);
                }
            } else {
                $form.hide();
                $container.find('[data-message-error]').removeClass('hide');
                $form.trigger('submit_error');
                if (typeof errorCallback === 'function') {
                    errorCallback(jqXHR, textStatus, errorThrown);
                }
            }
        },
    });
}

/**
 * Bind form to Ajax behavior.
 * @param  {jQueryNode} $form                      Form to bind
 * @param  {jQueryNode} $messageSuccess            Message to display on success
 * @param  {Boolean} autoSubmit                    Indicate auto submit or not
 * @param  {Function} onSuccess                    Callback on success
 * @param  {Function} onError                      Callback on HTTP error
 * @param  {Function} beforeSend                   Prepare data before send
 * @param  {String} submitSelector                 jQuery selector to find submit button
 * @param  {Function} manipulateValuesBeforeSubmit Change form values before submit
 */
export function bindAjaxForm(
    $form,
    $messageSuccess,
    autoSubmit,
    onSuccess,
    {
        onError = null,
        beforeSend,
        beforeSubmit,
        submitSelector = ':submit',
        manipulateValuesBeforeSubmit = null,
    } = {},
) {
    const $submitButton = $form.find(submitSelector);

    $form.on('submit', () => {
        $form.trigger(MAEvent.formSubmitAttempt);

        if (manipulateValuesBeforeSubmit && typeof manipulateValuesBeforeSubmit === 'function') {
            manipulateValuesBeforeSubmit();
        }
    });

    $form.one('change', () => {
        $form.trigger(MAEvent.formStarted);
    });

    if (autoSubmit === false) {
        return;
    }

    const ajaxSubmitOptions = {
        dataType: 'json',
        beforeSend() {
            $submitButton.attr('disabled', '');

            if (typeof beforeSend === 'function') {
                beforeSend();
            }
        },
        beforeSubmit(arr) {
            if (typeof beforeSubmit === 'function') {
                beforeSubmit(arr);
            }
            return true;
        },
        success(response, textStatus, jqXHR) {
            removeErrors($form);

            $form.trigger(MAEvent.formSubmitSuccess);

            if (response && response.redirect) {
                setTimeout(() => {
                    document.location.href = response.redirect;
                }, 0);
                return;
            }

            if ($messageSuccess && $messageSuccess.length > 0) {
                $form.addClass('hide');
                $messageSuccess.removeClass('hide');
            }

            if (typeof onSuccess === 'function') {
                onSuccess(response, textStatus, jqXHR);
            }
        },
        complete() {
            $submitButton.removeAttr('disabled');
        },
        error(jqXHR) {
            $form.trigger(MAEvent.formSubmitError);

            if (jqXHR.status === 400) {
                displayErrors($form, jqXHR.responseJSON);
            } else {
                $submitButton.removeAttr('disabled');
                global.console.error('Unknow error in Form.bindAjaxForm()');
            }

            if (typeof onError === 'function') {
                onError();
            }
        },
    };

    $form.ajaxForm(ajaxSubmitOptions);
}

/**
 * Bind form to Ajax behavior.
 * @param  {jQueryNode} $form           Form to bind
 * @param  {jQueryNode} $messageSuccess Message to display on success
 * @param  {Boolean} autoSubmit         Indicate auto submit or not
 * @param  {Function} onSuccess         Callback on success
 * @param  {Function} onError           Callback on HTTP error
 * @param  {Function} beforeSend        Prepare data before send
 * @param  {Function} getData           Prepare data before send
 * @param  {Function} submitSelector    jQuery selector to find submit button
 */
export function bindXHRForm(
    $form,
    $messageSuccess,
    onSuccess,
    { onError = null, beforeSend, getData, submitSelector = ':submit' } = {},
) {
    const $submitButton = $form.find(submitSelector);

    $form.on('submit', event => {
        event.preventDefault();

        $form.trigger(MAEvent.formSubmitAttempt);

        $submitButton.attr('disabled', '');

        if (typeof beforeSend === 'function') {
            beforeSend();
        }

        let data = new FormData($form[0]);
        if (typeof getData === 'function') {
            data = getData(data);
        }

        HTTPService.post($form.attr('action'), data)
            .then(response => {
                $submitButton.removeAttr('disabled');
                removeErrors($form);

                $form.trigger(MAEvent.formSubmitSuccess);

                if ($messageSuccess && $messageSuccess.length > 0) {
                    $form.addClass('hide');
                    $messageSuccess.removeClass('hide');
                }

                if (typeof onSuccess === 'function') {
                    onSuccess(response);
                }
            })
            .catch(error => {
                $submitButton.removeAttr('disabled');
                $form.trigger(MAEvent.formSubmitError);

                if (error.response && error.response.status === 400) {
                    displayErrors($form, error.response.data);
                } else {
                    $submitButton.removeAttr('disabled');
                    global.console.error('Unknow error in Form.bindXHRForm()');
                }

                if (typeof onError === 'function') {
                    onError(error.response);
                }
            });
    });

    $form.one('change', () => {
        $form.trigger(MAEvent.formStarted);
    });
}

/**
 *
 * @param {jQuery} $form The form containing the errors
 * @param {jQuery} $wrapper
 */
export function scrollToFirstError($form, $wrapper) {
    const firstFieldError = $form.find('.error').first();
    if (firstFieldError.length > 0) {
        if (typeof $wrapper === 'undefined') {
            window.scrollTo(0, firstFieldError.offset().top - 20);
        } else {
            $wrapper.scrollTop(firstFieldError.position().top - 20);
        }
    }
}

/**
 * Validate number field value
 * @param {String} fieldValue Field value
 * @param {Number} minNumber Min authorized value
 * @param {Number} maxNumber Max authorized value
 * @param {String} errorFieldName Field name displayed in error message
 * @param {String} unit Unit name displayed after number if there is one
 * @param {Boolean} boolean Whether error message should be shorter or not
 * @return {Boolean|String} True if valid else error string
 */
export function validateNumber(
    fieldValue,
    minNumber,
    maxNumber,
    errorFieldName = '',
    unit = '',
    shortenMsg,
) {
    const value = Number(fieldValue);

    if (fieldValue !== undefined && fieldValue !== '' && Number.isNaN(value)) {
        return `${errorFieldName} : Veuillez saisir une valeur`;
    }

    if (fieldValue !== undefined && fieldValue !== '' && !/^[^,.][0-9]*$/.test(value)) {
        return `${errorFieldName} : Veuillez saisir un nombre valide`;
    }

    if (
        Number.isInteger(minNumber) &&
        fieldValue !== undefined &&
        fieldValue !== '' &&
        value < minNumber
    ) {
        return `${shortenMsg ? 'Min.' : 'Valeur min.'} : ${Numeral(minNumber).format(
            '0,0',
        )}${unit}`;
    }

    if (
        Number.isInteger(maxNumber) &&
        fieldValue !== undefined &&
        fieldValue !== '' &&
        value > maxNumber
    ) {
        return `${shortenMsg ? 'Max.' : 'Valeur max.'} : ${Numeral(maxNumber).format(
            '0,0',
        )}${unit}`;
    }

    if (
        Number.isInteger(minNumber) &&
        Number.isInteger(maxNumber) &&
        fieldValue !== undefined &&
        fieldValue !== '' &&
        (value < minNumber || value > maxNumber)
    ) {
        return `${errorFieldName} : Veuillez saisir une valeur entre ${Numeral(minNumber).format(
            '0,0',
        )} et ${Numeral(maxNumber).format('0,0')}${unit}`;
    }

    return true;
}

/**
 * Validate min and max field, display error if any
 * @param {HTMLFormElement} form
 * @param {HTMLFormElement} fieldName Field name
 * @param {Number} minValue Min value of the field
 * @param {Number} maxValue Max value of the field
 * @param {String} errorFieldName Field name displayed in error message
 * @param {String} unit Unit for field name displayed in error message
 * @param {Boolean} shortenMsg To idnicate whether error message should be shorter or normal
 * @return {boolean}
 */
export function validateNumberField(
    form,
    fieldName,
    minValue,
    maxValue,
    errorFieldName,
    unit = '',
    shortenMsg = false,
) {
    const field = form.querySelector(`[name=${fieldName}]`);

    if (!field) {
        // Fields not found, bypass validation
        return true;
    }

    const validation = validateNumber(
        field.value,
        minValue,
        maxValue,
        errorFieldName,
        unit,
        shortenMsg,
    );

    if (validation !== true) {
        setFieldError($(field), [validation]);
        return false;
    }
    setFieldError($(field), []);

    return true;
}

/**
 * Validate 2 (not mandatory) fields values are in range (with not mandatory bounds)
 * @param {String} minField Min field value
 * @param {String} maxField Max field value
 * @param {Number} rangeMin Min authorized value
 * @param {Number} rangeMax Max authorized value
 * @return {Boolean|String} True if valid else error string
 */
export function validateRange(minField, maxField, rangeMin, rangeMax) {
    const minValue = Number(minField);
    const maxValue = Number(maxField);

    if (minField !== undefined && minField !== '' && Number.isNaN(minValue)) {
        return 'Veuillez saisir une valeur min';
    }
    if (maxField !== undefined && maxField !== '' && Number.isNaN(maxValue)) {
        return 'Veuillez saisir une valeur max';
    }

    if (minField !== undefined && minField !== '' && !/^[^,.][0-9]*$/.test(minField)) {
        return 'Veuillez saisir un nombre min valide';
    }
    if (maxField !== undefined && maxField !== '' && !/^[^,.][0-9]*$/.test(maxField)) {
        return 'Veuillez saisir un nombre max valide';
    }

    if (Number.isInteger(rangeMin) && minField !== undefined && minField && minValue < rangeMin) {
        return `Veuillez saisir une valeur supérieure à ${Numeral(rangeMin).format('0,0')}`;
    }
    if (
        Number.isInteger(rangeMax) &&
        maxField !== undefined &&
        maxField !== '' &&
        maxValue > rangeMax
    ) {
        return `Veuillez saisir une valeur inférieure à ${Numeral(rangeMax).format('0,0')}`;
    }
    if (
        minField !== undefined &&
        minField !== '' &&
        maxField !== undefined &&
        maxField !== '' &&
        minValue > maxValue
    ) {
        return 'Veuillez saisir une valeur minimum inférieure au maximum';
    }

    if (
        Number.isInteger(rangeMin) &&
        Number.isInteger(rangeMax) &&
        minField !== undefined &&
        minField !== '' &&
        maxField !== undefined &&
        maxField !== '' &&
        (minValue < rangeMin || maxValue > rangeMax)
    ) {
        return `Veuillez saisir une valeur entre ${Numeral(rangeMin).format('0,0')} et ${Numeral(
            rangeMax,
        ).format('0,0')}`;
    }

    return true;
}

/**
 * To test relationship in range - to use when individual fields already validated
 * @param {HTMLFormElement} form
 * @param {String} field Field name you want to validate
 * @param {String} errorMessage error message to display when max bigger than min
 */

export function validateRangeAfterInitialValidation(
    form,
    field,
    errorMessage = 'La valeur maximale doit être supérieure à la valeur minimale.',
) {
    const minField = form.querySelector(`[name=min_${field}]`);
    const maxField = form.querySelector(`[name=max_${field}]`);
    // use the name of field to select div where range error will be displayed
    const errorContainerDivSelector = `[data-control-${field}-range-errors]`;

    // remove any previous error from error container div
    removeError(errorContainerDivSelector);

    // if fields not filled AND are not equal to 0, bypass validation
    if ((!minField.value && minField.value !== 0) || (!maxField.value && maxField.value !== 0)) {
        return true;
    }

    const minFieldValue = minField ? Number(minField.value) : null;
    const maxFieldValue = maxField ? Number(maxField.value) : null;

    if (
        minFieldValue !== undefined &&
        Number.isInteger(minFieldValue) &&
        maxFieldValue !== undefined &&
        Number.isInteger(maxFieldValue)
    ) {
        // validation only if there is a value
        if (minFieldValue > maxFieldValue) {
            displayError(errorContainerDivSelector, errorMessage, true);
            return false;
        }
    }

    return true;
}

/**
 * Validate min and max field, display error if any
 * @param {HTMLFormElement} form
 * @param {HTMLFormElement} field
 * @param {Number} min Min value of the range
 * @param {Number} max Max value of the range
 * @return {boolean}
 */
export function validateRangeField(form, field, min, max) {
    const minField = form.querySelector(`[name=min_${field}]`);
    const maxField = form.querySelector(`[name=max_${field}]`);
    if (!minField || !maxField) {
        // Fields not found, bypass validation
        return true;
    }
    const validation = validateRange(minField.value, maxField.value, min, max);

    if (validation !== true) {
        setFieldError($(minField), [validation]);
    } else {
        setFieldError($(minField), []);
    }
    return !!validation;
}

/**
 * Update form UI to add throbers
 * @param {Boolean} pending For submission is pending ?
 * @param {Element} submitButton Form submit button
 * @param {Element} container List of results
 */
export function pendingFormUI(pending, submitButton, container) {
    /* eslint-disable no-param-reassign */
    if (pending) {
        if (submitButton) {
            submitButton.dataset.defaultLabel = submitButton.innerHTML;
            submitButton.innerHTML = spinnerTemplate({
                classes: 'spinner--dots-inline spinner--inverse',
            });
            addClass(submitButton, 'btn--pending');
            submitButton.setAttribute('disabled', '');
        }
        if (container) {
            addClass(container, 'pending--active');
        }
    } else {
        if (submitButton) {
            submitButton.innerHTML = submitButton.dataset.defaultLabel;
            removeClass(submitButton, 'btn--pending');
            submitButton.removeAttribute('disabled');
        }
        if (container) {
            removeClass(container, 'pending--active');
        }
    }
    /* eslint-enable no-param-reassign */
}
